import { environment } from "@app/relay/relay-environment";
import { useEffect, useState } from "react";
import { fetchQuery, graphql } from "react-relay";

import type { publicFactSheetPage_Query } from "@app/queries/publicFactSheetPage_Query.graphql";

// Operation name starts with "publicFactSheetPage" so the GraphQL controller
// serves it anonymously (no session required).
const factSheetQuery = graphql`
  query publicFactSheetPage_Query($slug: String!) {
    publicFactSheet(slug: $slug) {
      onWaitlist
      memberSince
      referralCode
      metrics {
        key
        label
        value
        unit
        benchmark
        benchmarkLabel
      }
    }
  }
`;

type Sheet = NonNullable<publicFactSheetPage_Query["response"]["publicFactSheet"]>;
type Metric = Sheet["metrics"][number];

export function formatMetric(value: number | null | undefined, unit: string): string {
  if (value == null) return "—";
  if (unit === "USD") {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
  }
  if (unit === "PERCENT") return `${value}%`;
  return `${value}`;
}

export default function PublicFactSheetPage({ slug, previewMetrics }: { slug?: string; previewMetrics?: readonly Metric[] }) {
  const [sheet, setSheet] = useState<Sheet | null | undefined>(previewMetrics ? null : undefined);

  useEffect(() => {
    if (!slug) return;
    fetchQuery<publicFactSheetPage_Query>(environment, factSheetQuery, { slug })
      .toPromise()
      .then((d) => setSheet(d?.publicFactSheet ?? null));
  }, [slug]);

  // Preview mode (owner): render the supplied metrics with placeholder header.
  const metrics = previewMetrics ?? sheet?.metrics ?? [];

  if (!previewMetrics && sheet === undefined) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0b1e3f] text-sm text-white/60">Loading…</div>;
  }
  if (!previewMetrics && sheet === null) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0b1e3f] text-sm text-white/60">No fact sheet found.</div>;
  }

  return (
    <div className="min-h-screen bg-[#0b1e3f] px-4 py-12 text-white">
      <div className="mx-auto max-w-4xl">
        <header className="mb-10 text-center">
          <h1 className="text-3xl font-bold">{previewMetrics ? "Your company" : "Company"}</h1>
          <p className="mt-2 text-sm text-white/60">
            {previewMetrics
              ? "Preview — this is what visitors will see"
              : sheet?.onWaitlist
                ? "On the QBO Sync waiting list"
                : `Member${sheet?.memberSince ? ` since ${sheet.memberSince}` : ""}`}
          </p>
        </header>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {metrics.map((metric) => (
            <div key={metric.key} className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
              <p className="text-xs font-medium uppercase tracking-wide text-white/50">{metric.label}</p>
              <p className="mt-2 text-3xl font-bold">{formatMetric(metric.value, metric.unit)}</p>
              {metric.benchmark != null && (
                <p className="mt-2 text-xs text-white/40">
                  Benchmark: {formatMetric(metric.benchmark, metric.unit)}
                  {metric.benchmarkLabel ? ` · ${metric.benchmarkLabel}` : ""}
                </p>
              )}
            </div>
          ))}
        </div>

        {metrics.length === 0 && (
          <p className="text-center text-sm text-white/50">This company hasn't shared any metrics yet.</p>
        )}

        {!previewMetrics && (
          <footer className="mt-12 text-center text-xs text-white/40">
            Powered by QBO Sync · Want your own fact sheet?{" "}
            <a href="/sign-up" className="underline">
              Start onboarding
            </a>
            {sheet?.referralCode ? ` (referral ${sheet.referralCode})` : ""}
          </footer>
        )}
      </div>
    </div>
  );
}
